import { OutlineFilter } from '@pixi/filter-outline'
import * as PIXI from 'pixi.js'
import { useCallback, useMemo, useRef } from 'react'
import { createSunParticle, createWaterParticle } from '../pixi/particles'
import { useGameStore } from '../store/gameStore'

const PARTICLE_THROTTLE_MS = 120
const PER_PLANT_PARTICLE_COOLDOWN_MS = 250
const MAX_GAMEPLAY_PARTICLES = 60
const getNow = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now())

export function usePlantInteractions({
  appRef,
  pendingSeedTypeRef,
  selectedModeRef,
  isAnimatingRef,
  setIsAnimating,
  setPendingSeeds,
  particlesContainerRef,
  onCancelSeed,
}) {
  const triggerPlantClickFeedback = useCallback((plantId) => {
    const appInstance = appRef.current
    if (!appInstance) return

    const plantContainer = appInstance.stage.children.find(
      (child) => child.name === 'plant' && child.userData && child.userData.plantId === plantId
    )
    if (!plantContainer) return

    plantContainer.userData = plantContainer.userData || {}

    if (!plantContainer.userData.baseScale) {
      plantContainer.userData.baseScale = {
        x: plantContainer.scale.x || 1,
        y: plantContainer.scale.y || 1,
      }
      plantContainer.userData.baseRotation = plantContainer.rotation || 0
    }

    plantContainer.userData.clickBoost = 1

    if (!plantContainer.userData.clickOutline) {
      const outline = new OutlineFilter(3, 0xffffff)
      plantContainer.userData.clickOutline = outline
      const existing = plantContainer.filters || []
      plantContainer.filters = [...existing, outline]
    }
  }, [appRef])

  const interactionPoint = useMemo(() => new PIXI.Point(), [])
  const expandedBounds = useMemo(() => new PIXI.Rectangle(), [])
  const lastParticleSpawnRef = useRef({ sun: 0, water: 0 })
  const plantParticleCooldownRef = useRef(new Map())

  const canSpawnParticle = useCallback((type, plantId) => {
    const now = getNow()
    const last = lastParticleSpawnRef.current[type] || 0
    if (now - last < PARTICLE_THROTTLE_MS) {
      return false
    }
    lastParticleSpawnRef.current[type] = now
    if (plantId) {
      const key = `${type}-${plantId}`
      const lastPlant = plantParticleCooldownRef.current.get(key) || 0
      if (now - lastPlant < PER_PLANT_PARTICLE_COOLDOWN_MS) {
        return false
      }
      plantParticleCooldownRef.current.set(key, now)
    }
    return true
  }, [])

  const handleInteraction = useCallback((e) => {
    e.preventDefault()

    const app = appRef.current
    if (!app) return

    const primaryTouch = e.touches?.[0]
    const clientX = e.clientX ?? primaryTouch?.clientX
    const clientY = e.clientY ?? primaryTouch?.clientY
    if (clientX == null || clientY == null) return

    app.renderer.plugins.interaction.mapPositionToPoint(interactionPoint, clientX, clientY)
    const stagePoint = app.stage.toLocal(interactionPoint)
    const worldX = stagePoint.x
    const worldY = stagePoint.y

    const mode = selectedModeRef.current
    const store = useGameStore.getState()

    // Interactions avec plantes existantes
    if (mode) {
      const plantsState = Array.isArray(store.plants) ? store.plants : []
      const plantsById = new Map(plantsState.map((plant) => [plant.id, plant]))
      let clickedPlantData = null

      for (let i = 0; i < app.stage.children.length; i += 1) {
        const child = app.stage.children[i]
        if (child.name !== 'plant' || !child.userData || !child.userData.plantId) {
          continue
        }

        const plantId = child.userData.plantId
        const plant = plantsById.get(plantId)
        if (!plant) continue

        const bounds = child.getBounds()
        let hit = bounds.contains(worldX, worldY)

        if (!hit) {
          const clicksPerStage = plant.config.clicksPerStage || 1
          const stage = Math.floor((plant.progress || 0) / clicksPerStage)

          let expandX = 16
          let expandY = 20
          if (stage <= 0) {
            expandX = 32
            expandY = 40
          }

          expandedBounds.x = bounds.x - expandX
          expandedBounds.y = bounds.y - expandY
          expandedBounds.width = bounds.width + expandX * 2
          expandedBounds.height = bounds.height + expandY * 2

          hit = expandedBounds.contains(worldX, worldY)
        }

        if (!hit) continue

        const centerX = bounds.x + bounds.width / 2
        const centerY = bounds.y + bounds.height / 2
        const dx = worldX - centerX
        const dy = worldY - centerY
        const distSq = dx * dx + dy * dy

        if (!clickedPlantData || distSq < clickedPlantData.distanceSq) {
          clickedPlantData = {
            plant,
            container: child,
            distanceSq: distSq,
          }
          if (distSq === 0) break
        }
      }

      if (clickedPlantData?.plant) {
        const clickedPlant = clickedPlantData.plant

        // Collecte de graine prioritaire
        if (clickedPlant.pendingSeed) {
          if (app.userData && app.userData.particles) {
            const indicator = new PIXI.Container()
            indicator.position.set(clickedPlant.position.x, clickedPlant.position.y - 90)

            const seed = new PIXI.Graphics()
            seed.beginFill(0xF4D03F)
            seed.drawCircle(0, 0, 8)
            seed.endFill()
            indicator.addChild(seed)

            const text = new PIXI.Text('+1', {
              fontFamily: 'Arial',
              fontSize: 14,
              fill: 0xffffff,
              fontWeight: 'bold',
            })
            text.position.set(-8, -18)
            indicator.addChild(text)

            indicator.alpha = 1
            app.stage.addChild(indicator)

            const particle = {
              graphics: indicator,
              alpha: 1,
              update() {
                if (!this.graphics || !this.graphics.parent) return
                this.graphics.position.y -= 0.7
                this.alpha -= 0.03
                this.graphics.alpha = this.alpha
              },
              isDead() {
                return this.alpha <= 0
              },
            }

            app.userData.particles.push(particle)
          }

          store.collectSeedFromPlant(clickedPlant.id)
          return
        }

        // Interaction selon le mode
        if (mode === 'sun') {
          const sunValue = clickedPlant.config.sunClickValue || 1
          store.addProgressToPlant(clickedPlant.id, sunValue)
          if (
            particlesContainerRef.current &&
            app.userData &&
            (app.userData.particles?.length || 0) < MAX_GAMEPLAY_PARTICLES &&
            canSpawnParticle('sun', clickedPlant.id)
          ) {
            createSunParticle(particlesContainerRef.current, worldX, worldY, app.userData.particles)
          }
          triggerPlantClickFeedback(clickedPlant.id)
        } else if (mode === 'water') {
          const waterLevelState = store.waterLevel
          if (waterLevelState >= 10) {
            const waterBonus = clickedPlant.config.waterClickBonus || 2
            store.addProgressToPlant(clickedPlant.id, waterBonus)
            store.waterPlant()
            if (
              particlesContainerRef.current &&
              app.userData &&
              (app.userData.particles?.length || 0) < MAX_GAMEPLAY_PARTICLES &&
              canSpawnParticle('water', clickedPlant.id)
            ) {
              createWaterParticle(particlesContainerRef.current, worldX, worldY, app.userData.particles)
            }
            triggerPlantClickFeedback(clickedPlant.id)
          }
        } else if (mode === 'hand') {
          store.destroyPlantById(clickedPlant.id)
        } else if (mode === 'pruner' && clickedPlantData.container) {
          let targetBranchKey = null

          for (let i = 0; i < clickedPlantData.container.children.length; i += 1) {
            const child = clickedPlantData.container.children[i]
            if (!child || !child.getBounds) continue
            const bounds = child.getBounds()
            if (!bounds.contains(worldX, worldY)) continue

            const key = child.userData && child.userData.branchKey
            if (key) {
              targetBranchKey = key
              break
            }
          }

          if (targetBranchKey) {
            store.prunePlantBranches(clickedPlant.id, targetBranchKey)
            triggerPlantClickFeedback(clickedPlant.id)
          }
        }
        return
      }
    }

    // Plantation d'une nouvelle graine
    if (pendingSeedTypeRef.current && !isAnimatingRef.current) {
      const currentInventory = store.seedInventory
      const seedType = pendingSeedTypeRef.current

      if (!currentInventory[seedType] || currentInventory[seedType] <= 0) {
        console.error('Plus de graines disponibles!')
        if (onCancelSeed) {
          onCancelSeed()
        }
        return
      }

      const newInventory = { ...currentInventory }
      newInventory[seedType] -= 1
      if (newInventory[seedType] === 0) {
        delete newInventory[seedType]
      }
      useGameStore.setState({ seedInventory: newInventory })

      const BASE_HEIGHT = 600
      const groundY = BASE_HEIGHT - 150
      setIsAnimating(true)

      const seed = new PIXI.Graphics()
      seed.beginFill(0x000000, 0.3)
      seed.drawCircle(2, 2, 12)
      seed.endFill()
      seed.beginFill(0x8B4513)
      seed.drawCircle(0, 0, 12)
      seed.endFill()
      seed.lineStyle(2, 0xFFFFFF, 1)
      seed.drawCircle(0, 0, 12)
      seed.position.set(worldX, 0)
      seed.name = 'animatingSeed'

      app.stage.addChild(seed)

      let currentY = 0
      let velocity = 0
      const gravity = 0.5

      const animate = () => {
        if (!seed.parent) return

        velocity += gravity
        currentY += velocity
        seed.position.y = currentY
        seed.rotation += 0.05

        if (currentY >= groundY) {
          seed.position.y = groundY
          seed.rotation = 0
          setTimeout(() => {
            setIsAnimating(false)
            setPendingSeeds((prev) => [
              ...prev,
              {
                position: { x: worldX, y: groundY },
                type: seedType,
                graphics: seed,
              },
            ])
          }, 100)
        } else {
          requestAnimationFrame(animate)
        }
      }

      animate()
      return
    }
  }, [
    appRef,
    canSpawnParticle,
    isAnimatingRef,
    onCancelSeed,
    particlesContainerRef,
    pendingSeedTypeRef,
    selectedModeRef,
    setIsAnimating,
    setPendingSeeds,
    triggerPlantClickFeedback,
  ])

  return { handleInteraction }
}
